<script lang="ts">
	import Modal from '$lib/modals/Modal.svelte'
	import Textfield from '@smui/textfield'
	import LoadingIcon from '$lib/components/account/LoadingIcon.svelte'
	import ErrorNotice from '../common/ErrorNotice.svelte'
	import {
		storeLoggedUser,
		storeLoginModal,
		storeProductToAdd,
		storeSnackbar,
		storeCustomerInfo,
		storeActiveWishlist,
		storeWishlists,
		storeCartItems,
	} from '$lib/js/stores'
	import { t } from 'svelte-i18n-lingui'
	import { getCustomerToken } from '$lib/js/magento/cart/cart'
	import { getCartProducts, synchronizeCart } from '$lib/js/cart'
	import { getContextScope } from '$lib/js/helpers/i18n'
	import { PUBLIC_MAGENTO_ENDPOINT } from '$env/static/public'
	import { connectToMagento, getCustomerInfo } from '$lib/js/magento/account/account'
	import { checkWishlistStatus } from '$lib/js/supabase/wishlists'
	import { socialLogin } from '$lib/js/helpers'

	const scope = getContextScope()

	let isRegistering = $state(false)
	let loading = $state(false)
	let errors = $state({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' })
	let values = $derived({
		email: '',
		password: '',
		confirmPassword: '',
		firstName: '',
		lastName: '',
	})

	let displayError = $state(false)
	let errorMessage = $state('')

	async function handleLogin() {
		try {
			let connectMagento = await connectToMagento({
				email: values.email,
				password: values.password,
			})

			const customerToken = await getCustomerToken()

			if (connectMagento.success && customerToken && customerToken.status === 'logged_in') {
				const customer = await getCustomerInfo(customerToken?.token)
				await synchronizeCart().then(async (info) => {
					$storeCartItems = await getCartProducts(info, { scope: scope })
				})
				$storeCustomerInfo = customer
				$storeLoggedUser = true
				$storeLoginModal = false

				// If there's a product to add to wishlist
				const isInWishlist = await checkWishlistStatus({ scope })

				if ($storeProductToAdd && !isInWishlist) {
					const response = await fetch(`/${scope}/endpoints/wishlist`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							email: values.email,
							sku: $storeProductToAdd.sku,
							action: 'add',
							wishlistId: $storeActiveWishlist.id,
						}),
					})
					const result = await response.json()

					if (!result.success) {
						throw new Error(result.error || 'Failed to update wishlist')
					}

					$storeActiveWishlist = result.data

					if (isRegistering) {
						$storeWishlists = [$storeActiveWishlist]
					}

					if (result.success) {
						$storeSnackbar.message =
							$t`Product added to wishlist` +
							` <a href="/${scope}/customer/wishlists">${$storeActiveWishlist.name}</a>`
						$storeSnackbar.show = true
					} else {
						$storeSnackbar.message = $t`Failed to add product to wishlist`
						$storeSnackbar.show = true
					}
					$storeProductToAdd = null
					loading = false
				}
			} else {
				loading = false
				displayError = true
				errorMessage = $t`Invalid email or password`
				return
			}
		} catch (err) {
			loading = false
			displayError = true
			errorMessage = $t`Invalid email or password`
		}
	}

	async function handleRegister() {
		try {
			if (values.password !== values.confirmPassword) {
				errors.confirmPassword = $t`Passwords do not match`
				return
			}

			if (!values.firstName || !values.lastName) {
				displayError = true
				errorMessage = $t`Please enter your first and last name`
				return
			}

			loading = true

			const response = await fetch(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/customers`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					customer: {
						email: values.email,
						firstname: values.firstName,
						lastname: values.lastName,
						extension_attributes: {
							is_subscribed: false,
						},
					},
					password: values.password,
				}),
			})

			const responseData = await response.json()

			if (!response.ok) {
				throw responseData
			}

			// After successful registration, log the user in
			$storeWishlists = []
			await handleLogin()
		} catch (err: any) {
			console.log('Registration error', err)
			loading = false
			displayError = true

			// Handle Magento error response
			if (err && err.message) {
				// If the error has parameters, replace them in the message
				if (err.parameters && Array.isArray(err.parameters)) {
					let message = err.message
					err.parameters.forEach((param: string, index: number) => {
						message = message.replace(`%${index + 1}`, param)
					})
					errorMessage = message
				} else {
					errorMessage = err.message
				}
			} else {
				// Fallback to generic error message
				errorMessage = $t`Registration failed. Please try again.`
			}
		}
	}

	async function handleSubmit() {
		displayError = false
		errors = { email: '', password: '', confirmPassword: '', firstName: '', lastName: '' }

		if (
			!values.email ||
			!values.password ||
			(isRegistering && (!values.firstName || !values.lastName))
		) {
			displayError = true
			errorMessage = $t`Please fill in all required fields`
			return
		}

		if (isRegistering) {
			await handleRegister()
		} else {
			await handleLogin()
		}
	}
</script>

<Modal on:close={() => ($storeLoginModal = false)}>
	<div class="modal">
		<h1>{isRegistering ? $t`Create Account` : $t`Login`}</h1>
		<p>{$t`Access or register to add items to a wishlists and manage them.`}</p>

		{#if loading}
			<div class="loading-container">
				<LoadingIcon />
			</div>
		{:else}
			<form class="login-form" onsubmit={(e: Event) => {
				e.preventDefault()
				handleSubmit()
			}}>
				{#if isRegistering}
					<div class="u-input">
						<Textfield
							style="width: 100%;"
							bind:value={values.firstName}
							label={$t`First Name`}
							type="text"
							required
						/>
						<ErrorNotice error={errors.firstName} />
					</div>

					<div class="u-input">
						<Textfield
							style="width: 100%;"
							bind:value={values.lastName}
							label={$t`Last Name`}
							type="text"
							required
						/>
						<ErrorNotice error={errors.lastName} />
					</div>
				{/if}

				<div class="u-input">
					<Textfield
						style="width: 100%;"
						bind:value={values.email}
						label={$t`Email`}
						type="email"
						required
					/>
					<ErrorNotice error={errors.email} />
				</div>

				<div class="u-input">
					<Textfield
						style="width: 100%;"
						bind:value={values.password}
						label={$t`Password`}
						type="password"
						required
					/>
					<ErrorNotice error={errors.password} />
				</div>

				{#if isRegistering}
					<div class="u-input">
						<Textfield
							style="width: 100%;"
							bind:value={values.confirmPassword}
							label={$t`Confirm Password`}
							type="password"
							required
						/>
						<ErrorNotice error={errors.confirmPassword} />
					</div>
				{/if}

				{#if displayError}
					<ErrorNotice error={errorMessage} />
				{/if}

				{#if !isRegistering}
					<a
						href="/{scope}/customer/account/forgotpassword"
						target="_blank"
						rel="noopener noreferrer">{$t`Forgot your password?`}</a
					>
				{/if}

				<div class="buttons">
					<button class="u-btn" type="submit">
						{isRegistering ? $t`Register` : $t`Login`}
					</button>
					<button
						type="button"
						class="u-btn u-btn--light"
						onclick={() => (isRegistering = !isRegistering)}
					>
						{isRegistering ? $t`Already have an account? Login` : $t`Need an account? Register`}
					</button>
				</div>
			</form>

			<button onclick={() => socialLogin('google')} class="u-btn u-btn--alt social-login google"
				><img
					src="/images/icons/google-icon.png"
					alt="Google Social Login"
					title="Google Social Login"
				/>{$t`Login with Google`}</button
			>

			<button onclick={() => socialLogin('facebook')} class="u-btn u-btn--alt social-login facebook"
				><img
					src="/images/icons/facebook-icon.png"
					alt="Facebook Social Login"
					title="Facebook Social Login"
				/>{$t`Login with Facebook`}</button
			>
		{/if}
	</div>
</Modal>

<style lang="scss">
	.social-login {
		display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px 0 8px;
    width: 100%;
		margin-top: 10px;

		&.google {
			margin-top: 20px;
		}

		img {
			width: 38px;
			margin-right: 6px;
		}
	}

	.loading-container {
		height: 100%;
		width: 240px;
		height: 300px;
	}

	.modal {
		max-width: 400px;
		margin: 0 auto;

		h1 {
			font-size: 24px;
			margin-bottom: 12px;
		}

		p {
			margin-bottom: 20px;
		}

		.login-form {
			.u-input {
				margin-bottom: 20px;
			}

			.buttons {
				display: flex;
				flex-direction: column;
				gap: 10px;
				margin-top: 20px;

				button {
					width: 100%;
				}
			}
		}
	}
</style>
